import { useQuery } from 'react-query';
import axios, { AxiosResponse } from 'axios';
import { TIMESHEET_PACKAGE } from 'api/use-query-constants';
import ReactQueryHook from 'utils/react-query-hook';
import { IOnlineApprovalTimeEntry } from '../models/online-approval-time-entry.model';
import { useTrackCotapEvent } from '../../app-insights/track-event.hook';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/root-reducer';
import { AppConfig } from '../../config/config.model';
import { TimesheetPackage, TimesheetRelatedEntity } from '../models/timesheet-package.model';

const useTimeEntries = (timesheetId: string, url: string): ReactQueryHook<TimesheetPackage> => {
    const trackError = useTrackCotapEvent('OnlineApprovalTimeEntries Error', false);
    const config = useSelector((state: RootState) => state.globalElements.appConfig as AppConfig);
    const isUsingV2Endpoints = useSelector((state: RootState) => state.globalElements.isUsingV2Endpoints);

    const {
        isLoading,
        error,
        data,
    }: {
        isLoading: boolean;
        error?: unknown;
        data?: TimesheetPackage;
    } = useQuery(TIMESHEET_PACKAGE, () => {
        if (isUsingV2Endpoints) {
            return axios
                .post(`${config.TIMESHEET_SERVICE_BASE_URL}/TimesheetApproval/LoadTimesheetPackage`, {
                    timesheetId,
                    onlyActiveOnlineApprovalTimeEntries: true,
                    relatedEntities: [
                        TimesheetRelatedEntity.OnlineApprovalTimeEntry,
                        TimesheetRelatedEntity.CostCenters,
                    ],
                    cotaSecret: url,
                })
                .then((response: AxiosResponse<TimesheetPackage>) => response?.data)
                .catch((e: Error) => {
                    trackError({ e });
                    throw e;
                });
        }

        return axios
            .get(
                `${config.TIMESHEET_SERVICE_BASE_URL}/TimesheetApproval/OnlineApprovalTimeEntries?clientOnlineTimesheetApprovalSecret=${url}&timesheetId=${timesheetId}`,
            )
            .then((response: AxiosResponse<IOnlineApprovalTimeEntry[]>) => {
                const onlineApprovalTimeEntries = response?.data as IOnlineApprovalTimeEntry[];
                return { onlineApprovalTimeEntries: onlineApprovalTimeEntries };
            })
            .catch((e: Error) => {
                trackError({ e });
                throw e;
            });
    });

    return new ReactQueryHook<TimesheetPackage>(isLoading, error, data);
};

export { useTimeEntries };
